import { keepPreviousData } from "@tanstack/react-query";

import { useAsyncDataFromQuery } from "@tsmono/react/hooks";
import { AsyncData } from "@tsmono/util";

import { EvalLogStatus } from "../@types/extraInspect";
import { sampleIdsEqual } from "../app/shared/sample";
import {
  Log,
  LogHeader,
  SampleDerived,
  SampleSummary,
} from "../client/api/types";
import { SampleSummariesScope } from "../client/database";
import { PreparedSampleSummary } from "../client/utils/type-utils";
import { queryClient } from "../state/queryClient";

import { currentDatabase } from "./databaseInstance";

/**
 * The scoped samples read: sample summaries under a scope — one log file
 * (the log view's sample list) or a path prefix (samples mode) — each row
 * carrying its log's display context, joined here so consumers never join by
 * name. Db-backed queryFns, invalidation-driven (the ingestion sink pushes
 * observed file scopes and invalidates the rest), `keepPreviousData` across
 * refetches, GC-able when unobserved.
 *
 * Paged queryFn shape from day one: `limit`/`cursor` participate in the query
 * key and read, so flipping the listing to pages is a queryFn swap, not a
 * contract change. Phase-2 consumers read one big page.
 */

export type SamplesScope = SampleSummariesScope;

export interface SamplesListingLogContext {
  created?: string;
  task?: string;
  model?: string;
  status?: EvalLogStatus;
}

export interface SamplesListingRow {
  logFile: string;
  summary: SampleSummary;
  derived: SampleDerived;
  log: SamplesListingLogContext;
}

export interface SamplesListingParams {
  logDir: string;
  scope: SamplesScope;
  limit?: number;
  cursor?: string;
}

const EMPTY_ROWS: SamplesListingRow[] = [];

/** The whole key family for a dir — the invalidation target. */
export const samplesListingDirKey = (logDir: string) =>
  ["log_data", "samples", logDir] as const;

export const samplesListingKey = (params: SamplesListingParams) =>
  [
    ...samplesListingDirKey(params.logDir),
    params.scope,
    params.limit ?? null,
    params.cursor ?? null,
  ] as const;

const logContext = (header: LogHeader | undefined): SamplesListingLogContext =>
  header === undefined
    ? {}
    : {
        created: header.eval.created,
        task: header.eval.task,
        model: header.eval.model,
        status: header.status,
      };

const rowContext = (row: Log | undefined): SamplesListingLogContext =>
  row === undefined
    ? {}
    : {
        created: row.header?.eval.created,
        task: row.task ?? undefined,
        model: row.model,
        status: row.status,
      };

/** Assemble listing rows from a prepared payload's parts (the sink's push
 *  path — the db read below produces the same shape). */
export const toSamplesListingRows = (
  logFile: string,
  header: LogHeader,
  summaries: PreparedSampleSummary[]
): SamplesListingRow[] =>
  summaries.map(({ summary, derived }) => ({
    logFile,
    summary,
    derived,
    log: logContext(header),
  }));

const pageOf = (
  rows: SamplesListingRow[],
  params: SamplesListingParams
): SamplesListingRow[] => {
  const start = params.cursor === undefined ? 0 : Number(params.cursor);
  return params.limit === undefined && start === 0
    ? rows
    : rows.slice(
        start,
        params.limit === undefined ? undefined : start + params.limit
      );
};

const readSamplesListing = async (
  params: SamplesListingParams
): Promise<SamplesListingRow[]> => {
  const db = currentDatabase();
  if (!db) {
    // Db-less sessions: the sink's pushes are the only source — behave as a
    // passive cache container (returning the current value, not []) so a
    // mount-time fetch settling after a push can't clobber it.
    return (
      queryClient.getQueryData<SamplesListingRow[]>(
        samplesListingKey(params)
      ) ?? EMPTY_ROWS
    );
  }
  const records = await db.readSampleSummaries(params.scope);
  const files = [...new Set(records.map((record) => record.file_path))];
  const rows = await db.readLogRows(files);
  const contexts = new Map(files.map((file) => [file, rowContext(rows[file])]));
  return pageOf(
    records.map((record) => ({
      logFile: record.file_path,
      summary: record.summary,
      derived: record.derived,
      log: contexts.get(record.file_path) ?? {},
    })),
    params
  );
};

/**
 * File scopes must use the LISTED name (the key the store and the sink's
 * pushes use) — callers with a route-form file resolve it first
 * (`resolveLogKey`). Prefix scopes are absolute paths.
 */
export const useSamplesListing = (
  params: SamplesListingParams
): AsyncData<SamplesListingRow[]> =>
  useAsyncDataFromQuery({
    queryKey: samplesListingKey(params),
    queryFn: () => readSamplesListing(params),
    staleTime: Infinity,
    placeholderData: keepPreviousData,
  });

/**
 * Non-React snapshot of a file's settled summaries (no pending merge) — for
 * the running-sample tick/finalize decisions. `logFile` must be the listed
 * name. Reads the store; falls back to the observed file-scope cache entry
 * in db-less sessions.
 */
export const readSettledSummaries = async (
  logDir: string,
  logFile: string
): Promise<SampleSummary[]> => {
  const scope: SamplesScope = { file: logFile };
  const db = currentDatabase();
  if (db) {
    const records = await db.readSampleSummaries(scope);
    return records.map((record) => record.summary);
  }
  const cached = queryClient.getQueryData<SamplesListingRow[]>(
    samplesListingKey({ logDir, scope })
  );
  return cached?.map((row) => row.summary) ?? [];
};

/** Whether one settled sample is complete, without materializing its file's
 *  full summary list when IndexedDB is available. */
export const hasCompletedSettledSummary = async (
  logDir: string,
  logFile: string,
  id: string | number,
  epoch: number
): Promise<boolean> => {
  const db = currentDatabase();
  if (db) {
    return db.hasCompletedSampleSummary(logFile, id, epoch);
  }
  const summaries = await readSettledSummaries(logDir, logFile);
  return summaries.some(
    (summary) =>
      sampleIdsEqual(summary.id, id) &&
      summary.epoch === epoch &&
      summary.completed !== false
  );
};

// ---------------------------------------------------------------------------
// Sink integration (called by the logsContent seam at ingestion time).
// ---------------------------------------------------------------------------

/**
 * Push fresh rows into a file's observed default-page listing entry WITHOUT
 * creating one (same guard as the per-handle detail pushes). This keeps the
 * log view's running→complete handoff atomic: the status flip (header push)
 * and the settled rows land in the same update, so a sample that existed
 * only in the pending buffer never vanishes while an invalidation refetch is
 * in flight. In db-less sessions this push is the only source of settled
 * rows.
 */
export const pushFileSamples = async (
  logDir: string,
  logFile: string,
  rows: SamplesListingRow[]
): Promise<void> => {
  const key = samplesListingKey({ logDir, scope: { file: logFile } });
  if (!queryClient.getQueryState(key)) {
    return;
  }
  // A mount-time fetch still in flight would commit over this push (it read
  // the store before these rows existed) — cancel it first.
  await queryClient.cancelQueries({ queryKey: key, exact: true });
  queryClient.setQueryData<SamplesListingRow[]>(key, rows);
};

/** Mark every samples listing under the dir stale and refetch the observed
 *  ones (Dexie-backed, so a refetch is a local read). */
export const invalidateSamplesListings = (logDir: string): void => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  queryClient.invalidateQueries({
    queryKey: samplesListingDirKey(logDir),
  });
};

/** Drop every samples listing entry under the dir (clear-all path). */
export const removeSamplesListings = (logDir: string): void => {
  queryClient.removeQueries({ queryKey: samplesListingDirKey(logDir) });
};
